import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Ban, Check, CheckCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { mobileUsersAPI, depositsAPI, withdrawalsAPI } from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

export default function PlayerDetail({ user, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletForm, setWalletForm] = useState({ amount: '', bucket: 'deposit', direction: 'credit', note: '' });
  const [note, setNote] = useState('');
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState('');
  const [shot, setShot] = useState(null);
  const [shotTitle, setShotTitle] = useState('Document');

  const userId = user.user_id;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await mobileUsersAPI.dossier(userId);
      setData(res.data);
    } catch {
      toast.error('Failed to load player');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const player = data?.user || user;
  const wallet = data?.wallet || {};
  const profile = data?.profile || {};
  const bank = data?.bank;
  const stats = data?.stats || {};
  const settings = data?.settings;

  const setKyc = async (status) => {
    const why = status === 'rejected' ? (window.prompt('Rejection reason') || 'Rejected by admin') : '';
    try {
      await mobileUsersAPI.setKyc(userId, { status, reason: why });
      toast.success('KYC updated');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'KYC update failed');
    }
  };

  const adjustWallet = async () => {
    const amount = Number(walletForm.amount);
    if (!amount || !walletForm.note.trim()) {
      toast.error('Enter an amount and a note');
      return;
    }
    try {
      await mobileUsersAPI.adjustWallet(userId, {
        amount: walletForm.direction === 'debit' ? -Math.abs(amount) : Math.abs(amount),
        bucket: walletForm.bucket,
        note: walletForm.note,
      });
      toast.success('Wallet updated');
      setWalletForm({ amount: '', bucket: 'deposit', direction: 'credit', note: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Wallet update failed');
    }
  };

  const saveNote = async () => {
    try {
      await mobileUsersAPI.addNote(userId, note);
      setNote('');
      toast.success('Note saved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save note');
    }
  };

  const toggleBan = async () => {
    try {
      if (player.is_banned) {
        await mobileUsersAPI.unban(userId);
        toast.success('Player unbanned');
      } else {
        const why = window.prompt('Ban reason', 'Banned by admin') || 'Banned by admin';
        await mobileUsersAPI.ban(userId, why);
        toast.success('Player banned and disconnected');
      }
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const approveDeposit = async (id) => {
    try {
      await depositsAPI.approve(id);
      toast.success('Deposit credited');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approve failed');
    }
  };

  const approveWithdrawal = async (row) => {
    try {
      await withdrawalsAPI.approve(row.id || row.withdrawal_id);
      toast.success('Withdrawal approved');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approve failed');
    }
  };

  const confirmReject = async () => {
    try {
      if (reject.kind === 'deposit') await depositsAPI.reject(reject.id, reason);
      else await withdrawalsAPI.reject(reject.id, reason);
      toast.success(reject.kind === 'withdrawal' ? 'Rejected and refunded' : 'Deposit rejected');
      setReject(null);
      setReason('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reject failed');
    }
  };

  const openShot = async (depositId) => {
    try {
      const res = await depositsAPI.getById(depositId);
      const raw = res.data.screenshot_url || '';
      if (!raw) {
        toast.error('No screenshot');
        return;
      }
      setShotTitle('Payment screenshot');
      setShot(raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`);
    } catch {
      toast.error('Could not load screenshot');
    }
  };

  const openKyc = async (docId, side, title) => {
    try {
      const res = await mobileUsersAPI.kycImage(userId, docId, side);
      const raw = res.data.image || '';
      if (!raw) {
        toast.error('No image');
        return;
      }
      setShotTitle(title);
      setShot(raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`);
    } catch {
      toast.error('Could not load document');
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div>
      <Button variant="ghost" className="mb-4" onClick={onBack}>
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to players
      </Button>
      <div className="page-header flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="page-title">{profile.name || player.name || player.phone || 'Player'}</h1>
          <p className="page-subtitle font-mono">{player.phone} · {userId}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Joined {formatDateTime(player.created_at)}
            {profile.email ? ` · ${profile.email}` : ''}
            {player.kyc_status ? ` · KYC ${player.kyc_status}` : ''}
          </p>
        </div>
        <Button variant={player.is_banned ? 'outline' : 'destructive'} onClick={toggleBan}>
          {player.is_banned ? <CheckCircle className="w-4 h-4 mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
          {player.is_banned ? 'Unban' : 'Ban'}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Deposit balance" value={formatCurrency(wallet.deposit_balance || 0)} />
        <Stat label="Winnings" value={formatCurrency(wallet.winnings_balance || 0)} />
        <Stat label="Total" value={formatCurrency(wallet.total_balance || 0)} />
        <Stat label="Games" value={stats.total_games || 0} />
        <Stat label="Wins / losses" value={`${stats.wins || 0} / ${stats.losses || 0}`} />
        <Stat label="Win rate" value={`${stats.win_rate || 0}%`} />
        <Stat label="Net from games" value={formatCurrency(stats.total_earnings || 0)} />
        <Stat label="Approved deposits" value={formatCurrency(stats.deposits_total || 0)} />
        <Stat label="Approved withdrawals" value={formatCurrency(stats.withdrawals_total || 0)} />
        <Stat label="Status" value={player.is_banned ? 'Banned' : 'Active'} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-card border border-border flex flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deposits">Deposits</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="kyc">KYC</TabsTrigger>
          <TabsTrigger value="wallet">Ledger</TabsTrigger>
          <TabsTrigger value="games">Games</TabsTrigger>
          <TabsTrigger value="activity">Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Profile">
              <Row k="Name" v={profile.name || player.name || '—'} />
              <Row k="Phone" v={player.phone} />
              <Row k="Email" v={profile.email || player.email || '—'} />
              <Row k="User ID" v={userId} />
              <Row k="Banned" v={player.is_banned ? (player.ban_reason || 'Yes') : 'No'} />
              <Row k="Sound" v={settings ? (settings.sound_enabled ? 'On' : 'Off') : '—'} />
              <Row k="Notifications" v={settings ? (settings.notifications_enabled ? 'On' : 'Off') : '—'} />
              <Row k="Language" v={settings?.language || '—'} />
            </Panel>
            <Panel title="Withdrawal bank">
              {bank ? (
                <>
                  <Row k="Holder" v={bank.account_holder} />
                  <Row k="Account" v={bank.account_number} />
                  <Row k="IFSC" v={bank.ifsc_code} />
                  <Row k="Bank" v={bank.bank_name} />
                </>
              ) : <p className="text-sm text-muted-foreground">No bank details saved.</p>}
            </Panel>
            <Panel title="Adjust wallet">
              <div className="grid grid-cols-2 gap-2">
                <Select value={walletForm.direction} onValueChange={(v) => setWalletForm({ ...walletForm, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="debit">Debit</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={walletForm.bucket} onValueChange={(v) => setWalletForm({ ...walletForm, bucket: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit balance</SelectItem>
                    <SelectItem value="winnings">Winnings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input className="mt-2" type="number" placeholder="Amount" value={walletForm.amount} onChange={(e) => setWalletForm({ ...walletForm, amount: e.target.value })} />
              <Textarea className="mt-2" rows={2} placeholder="Why this change" value={walletForm.note} onChange={(e) => setWalletForm({ ...walletForm, note: e.target.value })} />
              <Button className="mt-2" onClick={adjustWallet}>Apply</Button>
            </Panel>
            <Panel title="Admin notes">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note" />
              <Button className="mt-2" variant="outline" onClick={saveNote} disabled={note.trim().length < 2}>Save note</Button>
              <div className="mt-3 space-y-2">
                {(player.admin_notes || []).slice().reverse().map((n) => (
                  <div key={n.id} className="text-sm p-2 bg-muted rounded-lg">
                    <p>{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDateTime(n.created_at)}</p>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="deposits">
          <SimpleTable
            rows={data?.deposits || []}
            empty="No deposits"
            columns={['When', 'Amount', 'Method', 'Status', 'Proof', 'Actions']}
            render={(d) => (
              <>
                <TableCell>{formatDateTime(d.created_at)}</TableCell>
                <TableCell className="font-mono">{formatCurrency(d.amount)}</TableCell>
                <TableCell>{d.payment_method || 'upi'}</TableCell>
                <TableCell className="capitalize">{String(d.status || '').replace(/_/g, ' ')}</TableCell>
                <TableCell>
                  {d.has_screenshot ? <Button size="sm" variant="outline" onClick={() => openShot(d.deposit_id)}>View</Button> : '—'}
                </TableCell>
                <TableCell>
                  {(d.status === 'pending' || d.status === 'under_review') && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-emerald-400" onClick={() => approveDeposit(d.deposit_id)}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-400" onClick={() => { setReject({ kind: 'deposit', id: d.deposit_id }); setReason(''); }}><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="withdrawals">
          <SimpleTable
            rows={data?.withdrawals || []}
            empty="No withdrawals"
            columns={['When', 'Amount', 'Bank', 'Status', 'Actions']}
            render={(w) => (
              <>
                <TableCell>{formatDateTime(w.created_at)}</TableCell>
                <TableCell className="font-mono">{formatCurrency(w.amount)}</TableCell>
                <TableCell>{w.bank_name || '—'} {w.account_number ? `····${w.account_number}` : ''}</TableCell>
                <TableCell className="capitalize">{w.status}</TableCell>
                <TableCell>
                  {w.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-emerald-400" onClick={() => approveWithdrawal(w)}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-400" onClick={() => { setReject({ kind: 'withdrawal', id: w.id || w.withdrawal_id }); setReason(''); }}><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="kyc" className="mt-4 space-y-4">
          <Panel title={`KYC status: ${player.kyc_status || 'not set'}`}>
            <p className="text-sm text-muted-foreground mb-3">{player.kyc_reason || 'Set verification for this player. The app reads this on the next login.'}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setKyc('approved')}>Approve KYC</Button>
              <Button variant="outline" onClick={() => setKyc('pending')}>Mark pending</Button>
              <Button variant="destructive" onClick={() => setKyc('rejected')}>Reject KYC</Button>
            </div>
          </Panel>
          <SimpleTable
            rows={data?.kyc_documents || []}
            empty="No uploaded KYC documents"
            columns={['When', 'Name', 'PAN', 'Aadhaar', 'Status', 'Documents']}
            render={(doc) => (
              <>
                <TableCell>{formatDateTime(doc.created_at)}</TableCell>
                <TableCell>{doc.full_name || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{doc.pan_number || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{doc.aadhaar_number || '—'}</TableCell>
                <TableCell className="capitalize">{doc.status}{doc.rejection_reason ? ` · ${doc.rejection_reason}` : ''}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {doc.has_pan && <Button size="sm" variant="outline" onClick={() => openKyc(doc.id, 'pan', 'PAN')}>PAN</Button>}
                    {doc.has_aadhaar_front && <Button size="sm" variant="outline" onClick={() => openKyc(doc.id, 'aadhaar_front', 'Aadhaar front')}>Aadhaar front</Button>}
                    {doc.has_aadhaar_back && <Button size="sm" variant="outline" onClick={() => openKyc(doc.id, 'aadhaar_back', 'Aadhaar back')}>Aadhaar back</Button>}
                  </div>
                </TableCell>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="wallet">
          <SimpleTable
            rows={data?.transactions || []}
            empty="No ledger entries"
            columns={['When', 'Type', 'Amount', 'Balance after', 'Note']}
            render={(tx) => (
              <>
                <TableCell>{formatDateTime(tx.created_at)}</TableCell>
                <TableCell className="capitalize">{tx.type}</TableCell>
                <TableCell className="font-mono">{formatCurrency(tx.amount)}</TableCell>
                <TableCell className="font-mono">{tx.balance_after != null ? formatCurrency(tx.balance_after) : '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{tx.description}</TableCell>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="games">
          <SimpleTable
            rows={data?.games || []}
            empty="No games yet"
            columns={['When', 'Mode', 'Result', 'Fee', 'Opponent', 'Table']}
            render={(g) => (
              <>
                <TableCell>{formatDateTime(g.created_at)}</TableCell>
                <TableCell className="capitalize">{g.game_type}{g.pool_limit ? ` ${g.pool_limit}` : ''}</TableCell>
                <TableCell className="capitalize">{g.result}</TableCell>
                <TableCell className="font-mono">{formatCurrency(g.entry_fee)}</TableCell>
                <TableCell>{g.opponent_name || '—'}</TableCell>
                <TableCell className="font-mono text-xs">{g.table_id || '—'}</TableCell>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="activity" className="space-y-4 mt-4">
          <Panel title="Recent sessions">
            {(data?.sessions || []).length === 0 ? <p className="text-sm text-muted-foreground">No sessions.</p> : (data.sessions || []).map((s, i) => (
              <Row key={s.id || i} k={formatDateTime(s.created_at)} v={s.revoked ? 'Revoked' : 'Active'} />
            ))}
          </Panel>
          <Panel title="Notifications">
            {(data?.notifications || []).length === 0 ? <p className="text-sm text-muted-foreground">No notifications.</p> : (data.notifications || []).map((n) => (
              <div key={n.notification_id || n.id} className="py-2 border-b border-border text-sm">
                <p className="font-medium">{n.title || n.type}</p>
                <p className="text-muted-foreground">{n.body || n.message}</p>
              </div>
            ))}
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={!!reject} onOpenChange={() => setReject(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject {reject?.kind}</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!reason.trim()} onClick={confirmReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shot} onOpenChange={() => setShot(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{shotTitle}</DialogTitle></DialogHeader>
          {shot && <img src={shot} alt="Deposit proof" className="w-full rounded-lg border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat-card">
      <p className="stat-label">{label}</p>
      <p className="stat-value text-lg">{value}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="stat-card">
      <h3 className="font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/60">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium text-right break-all">{v || '—'}</span>
    </div>
  );
}

function SimpleTable({ rows, columns, render, empty }) {
  return (
    <div className="data-table-container mt-4">
      <Table>
        <TableHeader>
          <TableRow className="table-header">
            {columns.map((c) => <TableHead key={c}>{c}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={columns.length} className="text-center py-10 text-muted-foreground">{empty}</TableCell></TableRow>
          ) : rows.map((row, i) => (
            <TableRow key={row.txn_id || row.deposit_id || row.id || row.notification_id || i}>{render(row)}</TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
